import React from 'react'
import styled from '@emotion/styled'
import { Icon } from '@mobi/component-library/Assets/Icon'
import { colors, font, radius, shadow, spacing } from '@mobi/component-library/Foundations'
import { useExpandToggle } from '@mobi/utils/hooks/useExpandToggle'

type AccordionProps = {
  title: React.ReactNode
  children: React.ReactNode
  contentLeft?: React.ReactNode
  contentRight?: React.ReactNode
  /** Triggered when the accordion is toggled */
  onToggle?: (isExpandedPrevious: boolean) => void
  /** Determines if the accordion starts off already expanded */
  shouldStartExpanded?: boolean
  /** Controls if main theme should used */
  theme?: 'primary' | 'secondary' | null
  /** Changes component to controlled with external state */
  isExpandedControlled?: boolean
}

export const Accordion = React.forwardRef<HTMLDivElement, AccordionProps>(
  (
    {
      title,
      children,
      contentLeft,
      contentRight,
      onToggle,
      shouldStartExpanded,
      theme = 'secondary',
      isExpandedControlled,
    },
    ref
  ) => {
    const id = React.useId()
    const isFirstRenderRef = React.useRef(true)
    const titleFullHeightElRef = React.useRef<HTMLSpanElement>(null)

    const { wrapperRef, contentRef, shouldRender, isExpanded, toggleExpanded } = useExpandToggle({
      timeoutMs: 400,
      shouldStartExpanded,
    })

    React.useEffect(() => {
      if (typeof isExpandedControlled !== 'boolean') return
      toggleExpanded(isExpandedControlled)
    }, [isExpandedControlled, toggleExpanded])

    // If shouldStartExpanded, prevent animation on mount
    React.useLayoutEffect(() => {
      if (!isFirstRenderRef.current || !shouldStartExpanded || !wrapperRef.current) return
      wrapperRef.current.style.height = 'auto'
      wrapperRef.current.style.opacity = '1'
      isFirstRenderRef.current = false
    }, [shouldStartExpanded, wrapperRef])

    // Handle content opacity toggling
    React.useEffect(() => {
      if (!wrapperRef.current) return
      wrapperRef.current.style.opacity = isExpanded ? '1' : '0'
    }, [wrapperRef, isExpanded])

    const buttonId = `${id}.button`
    const regionId = `${id}.region`

    const titleStyle: React.CSSProperties | undefined =
      shouldStartExpanded && isFirstRenderRef.current
        ? { maxHeight: 'unset' }
        : isExpanded
          ? { maxHeight: titleFullHeightElRef.current?.offsetHeight }
          : undefined

    return (
      <ContainerStyled
        ref={ref}
        data-theme={theme}
        data-is-expanded={isExpanded}
        data-has-content-left={!!contentLeft}
        data-testid='Accordion'
      >
        <header
          onClick={() => {
            onToggle?.(isExpanded)
            toggleExpanded()
          }}
          aria-expanded={isExpanded}
          aria-controls={regionId}
          id={buttonId}
          data-testid='Accordion.Header'
        >
          <span>
            <span>{contentLeft}</span>

            <span className='accordion-head__title' style={titleStyle}>
              <span>{title}</span>
              <span ref={titleFullHeightElRef} aria-hidden>
                {title}
              </span>
            </span>
          </span>

          <span>
            {contentRight}

            <Icon
              className='accordion-head__chevron'
              size='2.4rem'
              name='SolidChevronDown'
              color='inherit'
            />
          </span>
        </header>

        {(isExpanded || shouldRender) && (
          <section ref={wrapperRef} aria-labelledby={buttonId} id={regionId} role='region'>
            <div ref={contentRef} data-testid='Accordion.Content'>
              {children}
            </div>
          </section>
        )}
      </ContainerStyled>
    )
  }
)

Accordion.displayName = 'Accordion'

// ======
// Styles
// ======

const ContainerStyled = styled.article({
  '> header': {
    '--headerHeight': '4.8rem',

    boxSizing: 'border-box',
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: spacing.smx2,
    width: '100%',
    minHeight: 'var(--headerHeight, 0)',
    padding: `0 ${spacing.md}`,
    background: 0,
    border: 0,
    WebkitTapHighlightColor: colors.transparent,
    touchAction: 'manipulation',
    textAlign: 'left',

    '&:hover': { cursor: 'pointer' },

    '> span': {
      display: 'flex',
      overflow: 'hidden',
      minWidth: 0,

      '&:first-of-type': {
        // Content Left
        '> span:nth-of-type(1)': {
          display: 'flex',
          alignItems: 'center',
          minHeight: 'var(--headerHeight, 0)',
          maxHeight: 'var(--headerHeight, 0)',

          ':empty': { display: 'none' },
        },

        // Title
        '> .accordion-head__title': {
          flex: 1,
          position: 'relative',
          boxSizing: 'border-box',
          display: 'grid',
          gridTemplateColumns: '1fr',
          gridTemplateRows: '1fr',
          alignItems: 'start',

          minWidth: 0,
          minHeight: 'var(--headerHeight, 0)',
          maxHeight: 'var(--headerHeight, 0)',
          transition: 'max-height 400ms ease',

          fontFamily: font.family.primary,
          fontSize: font.size.lg.fontSize,
          letterSpacing: font.size.lg.letterSpacing,
          lineHeight: font.size.lg.lineHeight,
          fontWeight: font.weight.medium,
          color: colors.black,

          '> span': {
            gridArea: '1 / 1',
            transition: 'opacity 400ms ease',
            paddingBlock: `calc((var(--headerHeight, 0) - ${font.size.lg.lineHeight}) / 2)`,
          },

          '> span:first-of-type': {
            overflow: 'hidden',
            whiteSpace: 'nowrap',
            textOverflow: 'ellipsis',
            opacity: 1,
          },

          '> span:nth-of-type(2)': { opacity: 0 },
        },
      },

      // Content Right + Chevron
      '&:last-of-type': {
        flexShrink: 0,
        alignItems: 'center',
        justifyContent: 'flex-end',
        gap: spacing.smx2,
        minHeight: 'var(--headerHeight, 0)',

        '.accordion-head__chevron': {
          transform: 'rotate(0deg)',
          transition: 'all 400ms ease',
          color: colors.neutral[900],
        },
      },
    },
  },

  '> section': {
    overflow: 'hidden',
    height: 0,
    opacity: 0,
    transition: 'height 400ms ease, opacity 600ms ease',
    willChange: 'height',

    '> div': { paddingInline: spacing.md },
  },

  '&[data-is-expanded="true"]': {
    '> header > span:last-of-type .accordion-head__chevron': { transform: 'rotate(-180deg)' },

    '> header > span:first-of-type .accordion-head__title': {
      '> span:nth-of-type(1)': { opacity: 0 },
      '> span:nth-of-type(2)': { opacity: 1 },
    },
  },

  '&[data-theme="primary"]': {
    position: 'relative',
    borderBottom: `0.1rem solid ${colors.neutral[400]}`,

    '> section': {
      '> div': { paddingBottom: spacing.sm },
    },
  },

  '&[data-theme="secondary"]': {
    backgroundColor: colors.white,
    border: `1px solid ${colors.neutral[300]}`,
    borderRadius: radius.lgx3,
    boxShadow: shadow.xs,

    '> section': {
      borderTop: `0.1rem solid ${colors.neutral[300]}`,

      '> div': { paddingBlock: spacing.sm },
    },
  },

  '&[data-has-content-left="true"]': {
    '> header': {
      paddingLeft: spacing.smx2,

      '> span:first-of-type': { gap: '0.2rem' },
    },
  },
})
